import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import PropTypes from 'prop-types';

import { SESSIONSTORAGE_KEY_REWARDS_INFO } from 'constants/rewardsInfo';

import { trackEvent } from 'helpers/analytics';
import { clearCartCount } from 'actions/cart';
import { setLastPageWasOrderconfirmation } from 'store/ducks/history/actions';
import { fetchConfirmationPixelPayload, fetchOrdersByPurchaseId } from 'actions/checkout/purchase';
import { firePixelServer } from 'actions/pixelServer';
import marketplace from 'cfg/marketplace.json';
import Address from 'components/checkout/caap/address/Address';
import ContentBox from 'components/checkout/caap/ContentBox';
import DesktopOnly from 'components/checkout/caap/DesktopOnly';
import RightArrow from 'components/checkout/caap/RightArrow';
import { Row, RowSpan } from 'components/checkout/caap/Layout';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import VoiceOfCustomer from 'components/checkout/caap/typ/VoiceOfCustomer';
import DecoratedPage from 'components/DecoratedPage';
import { Loader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { NotesSection } from 'components/checkout/caap/TermsConditionsAndNotes';
import { pluralize } from 'helpers';
import { inIframe } from 'helpers/CheckoutUtils';
import { filter } from 'helpers/lodashReplacement';
import css from 'styles/containers/orderConfirmation.scss';
import { removeFromSessionStorage } from 'helpers/ClientUtils';

const { checkout: { typ: { contactUsVerbiage } } } = marketplace;

export class OrderConfirmation extends Component {
  componentDidMount() {
    const {
      fetchOrdersByPurchaseId,
      fetchConfirmationPixelPayload,
      params: { purchaseId },
      setLastPageWasOrderconfirmation,
      checkoutData: { purchaseData: { isOrderBeingPlaced } }
    } = this.props;

    setLastPageWasOrderconfirmation(true);

    trackEvent('TE_PV_ORDERCONFIRMATION');

    clearCartCount();

    fetchOrdersByPurchaseId(purchaseId, (response, pId) => {
      if (response && isOrderBeingPlaced === false) {
        fetchConfirmationPixelPayload(pId);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { firePixelServer, checkoutData: { purchaseData: { confirmationPixelPayload } } } = this.props;
    const { checkoutData: { purchaseData: { confirmationPixelPayload: prevConfirmationPixelPayload } } } = prevProps;
    if (confirmationPixelPayload && confirmationPixelPayload !== prevConfirmationPixelPayload) {
      // make order total available to mPulse
      window.orderTotal = confirmationPixelPayload.order.grandTotal;

      // fire pixel server
      firePixelServer('confirmation', confirmationPixelPayload);
    }
  }

  makeEGiftCard(order) {
    const { orderId, items } = order;
    const { emailAddress } = items[0].egcInfo;
    const titleLine = `${items.length} gift ${pluralize('card', items.length)} ${pluralize('is', items.length)} being sent to:`;
    const deliveryMsg = 'Estimated delivery is in 5 minutes, but in rare circumstances delivery may be delayed.';
    const { testId } = this.context;

    return (
      <div>
        <SmallScreenOnly>
          <ContentBox isFirst={true} isNonRadio={true} isNoRightMargin={true}>
            <strong>{titleLine}</strong>
            <div data-test-id={testId('egcEmailAddress')}>{emailAddress}</div>
            <div className={css.egcDelivery} data-test-id={testId('egcDeliveryMsg')}>
              {deliveryMsg}
            </div>
          </ContentBox>
          { this.makeReviewOrEditOrder(orderId) }
        </SmallScreenOnly>

        <DesktopOnly>
          <div>
            <strong>
              Order:&nbsp;
              <span className={css.orderId} data-test-id={testId('desktopOrderId')}>
                {orderId}
              </span>
            </strong>
            <ul>
              <li>
                {titleLine} <strong data-test-id={testId('egcEmailAddress')}>{emailAddress}</strong>
                <ul>
                  <li data-test-id={testId('egcDeliveryMsg')}>
                    {deliveryMsg}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </DesktopOnly>
      </div>
    );
  }

  makeOrder(order) {
    const { items } = order;
    const eGiftCards = filter(items, item => item.egcInfo);
    return eGiftCards.length ? this.makeEGiftCard(order) : this.makeRetail(order);
  }

  makePromise(earliestArrivalDate, latestArrivalDate) {
    if (earliestArrivalDate === latestArrivalDate) {
      return (
        <span className={css.dateRange}>{earliestArrivalDate}</span>
      );
    }

    return (
      <span className={css.dateRange}>{earliestArrivalDate} - {latestArrivalDate}</span>
    );
  }

  makeRetail(order) {
    const { orderId, items, shippingAddress } = order;
    const titleLine = `${items.length} ${pluralize('item', items.length)} will be shipped to`;
    const { testId } = this.context;
    const [ firstItem ] = items;
    const { shippingInfo: { earliestArrivalDate, latestArrivalDate } } = firstItem;

    return (
      <div>
        <SmallScreenOnly>
          <ContentBox isFirst={true} isNonRadio={true}>
            <strong data-test-id={testId('mobileTitleLine')}>{titleLine}</strong>
            <div className={css.addressBlock}>
              <Address address={shippingAddress} multiLineDisplay={true} />
            </div>
            <div className={css.estimatedDeliveryBlock}>
              <span className={css.title}>Estimated Delivery: </span>
              {this.makePromise(earliestArrivalDate, latestArrivalDate)}
            </div>
          </ContentBox>
          { this.makeReviewOrEditOrder(orderId) }
        </SmallScreenOnly>

        <DesktopOnly>
          <strong>
            Order:&nbsp;
            <span className={css.orderId} data-test-id={testId('desktopOrderId')}>
              {orderId}
            </span>
          </strong>
          <ul>
            <li data-test-id={testId('desktopTitleLine')}>
              {titleLine}: <Address address={shippingAddress} />
            </li>
          </ul>
        </DesktopOnly>
      </div>
    );
  }

  makeReviewOrEditOrder(orderId) {
    const { testId } = this.context;
    return (
      <ContentBox
        isLast={true}
        isNonRadio={true}
        showOnlySmallScreen={true}>
        <Link to={`/orders/${orderId}?referrer=thankyou`} data-test-id={testId('orderReviewLink')}>
          <RowSpan span="9">
            <strong>Review or edit your order</strong>
          </RowSpan>
          <RowSpan span="3">
            <RightArrow />
          </RowSpan>
        </Link>
      </ContentBox>
    );
  }

  render() {
    const { checkoutData: { purchaseData: { orderInfo } } } = this.props;
    const { testId } = this.context;

    if (!orderInfo) {
      return <Loader />;
    }

    const isInFrame = typeof window !== 'undefined' && inIframe(window);
    const hideHeaderFooter = isInFrame || 'MOBILE';
    const reviewLink = orderInfo.length > 1 ? '/account' : `/orders/${orderInfo[0].orderId}`;
    const reviewLinkText = orderInfo.length > 1 ? 'Review or edit your recent orders' : 'Review or edit your order';

    return (
      <SiteAwareMetadata>
        <DecoratedPage hideFooter={hideHeaderFooter} hideHeader={hideHeaderFooter}>
          <div className={css.orderConfirmationContainer}>
            <div className={css.wrapper}>
              <h1>Thank you!</h1>
              <p>Your order has been placed.</p>
              <p>An email confirmation has been sent with your order details.</p>

              {
                orderInfo.map((order, i) => (
                  <div key={`order-${i}`} className={css.orderBlock} data-test-id={testId('orderBlock')}>
                    { this.makeOrder(order) }
                  </div>
                ))
              }

              <DesktopOnly>
                <Link to={reviewLink} className={css.reviewOrders} data-test-id={testId('orderReviewLink')}>
                  {reviewLinkText}
                </Link>
                or &nbsp;
                <Link to="/?referrer=thankyou" data-test-id={testId('continueShoppingTop')}>Continue Shopping</Link>
              </DesktopOnly>
            </div>

            <DesktopOnly>
              <Row>
                <RowSpan span="7">
                  <div className={`${css.wrapper} ${css.contactUsBlock}`}>
                    <div>
                      Have questions?
                    </div>
                    <div>
                      {contactUsVerbiage}
                    </div>
                    <div>
                      You can also visit our help pages or <Link to="/c/contact-us">Contact Us</Link>
                    </div>
                    <div>
                      <Link to="/" className={css.continueShopping} data-test-id={testId('continueShoppingBottom')}>
                        Continue Shopping
                      </Link>
                      or &nbsp;
                      <Link
                        to="/logout?referrer=thankyou"
                        data-test-id={testId('logoutLink')}
                        onClick={() => {
                          removeFromSessionStorage(SESSIONSTORAGE_KEY_REWARDS_INFO);
                        }}>Logout</Link>
                    </div>
                  </div>
                </RowSpan>
                <RowSpan span="7">
                  <VoiceOfCustomer />
                </RowSpan>
              </Row>
            </DesktopOnly>
            <SmallScreenOnly>
              <Row>
                <RowSpan mobileSpan="12">
                  <ContentBox isSolo={true} isNonRadio={true}>
                    <VoiceOfCustomer />
                  </ContentBox>
                </RowSpan>
              </Row>
            </SmallScreenOnly>
            <NotesSection />
          </div>
        </DecoratedPage>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  const { checkoutData } = state;

  return {
    checkoutData
  };
}

OrderConfirmation.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  fetchOrdersByPurchaseId,
  fetchConfirmationPixelPayload,
  firePixelServer,
  setLastPageWasOrderconfirmation
})(OrderConfirmation);
